import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { FaArrowLeft, FaPlay, FaLock, FaClock, FaCheck } from 'react-icons/fa';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RecetarioFolder = () => {
  const navigate = useNavigate();
  const { folderId } = useParams();
  const [folder, setFolder] = useState(null);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [hasAccess, setHasAccess] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);

  useEffect(() => {
    fetchFolderData();
    fetchUser();
  }, [folderId]);

  const fetchFolderData = async () => {
    try {
      const response = await axios.get(`${API}/recipe-videos/folders/${folderId}/videos`);
      setFolder(response.data.folder);
      setVideos(response.data.videos || []);
    } catch (error) {
      console.error('Error fetching folder:', error);
      toast.error('Error al cargar la carpeta');
    } finally {
      setLoading(false);
    }
  };

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: false });
      setUser(response.data);
      checkAccess(response.data.id);
    } catch (error) {
      setUser(null);
      setHasAccess(false);
    }
  };

  const checkAccess = async (userId) => {
    try {
      const response = await axios.get(`${API}/recipe-videos/check-access/${folderId}?user_id=${userId}`);
      setHasAccess(response.data.has_access);
    } catch (error) {
      setHasAccess(false);
    }
  };

  const handleVideoClick = (video) => {
    if (hasAccess || folder?.is_free) {
      setSelectedVideo(video);
    } else if (!user) {
      toast.info('Inicia sesión para ver este contenido');
      navigate('/login');
    } else {
      toast.info('Necesitas comprar acceso a esta carpeta');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0a0a0a' }}>
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[var(--brand-primary)] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">Cargando videos...</p>
        </div>
      </div>
    );
  }

  if (!folder) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0a0a0a' }}>
        <div className="text-center">
          <p className="text-gray-400 mb-4">Carpeta no encontrada</p>
          <Button onClick={() => navigate('/recetarios')}>Volver a Recetarios</Button>
        </div>
      </div>
    );
  }

  // Si hay un video seleccionado, mostrar el reproductor
  if (selectedVideo) {
    return (
      <div className="min-h-screen py-8 px-4" style={{ backgroundColor: '#0a0a0a' }}>
        <div className="max-w-5xl mx-auto">
          <Button 
            variant="ghost" 
            onClick={() => setSelectedVideo(null)} 
            className="mb-6 text-gray-400 hover:text-white"
          >
            <FaArrowLeft className="mr-2" /> Volver a la lista
          </Button>

          {/* Video Player */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl overflow-hidden mb-6"
            style={{ background: 'rgba(255, 255, 255, 0.05)' }}
          >
            <div className="aspect-video bg-black">
              {selectedVideo.video_url.includes('youtube') || selectedVideo.video_url.includes('youtu.be') ? (
                <iframe
                  src={selectedVideo.video_url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                  className="w-full h-full"
                  allowFullScreen
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope"
                  title={selectedVideo.title}
                />
              ) : selectedVideo.video_url.includes('vimeo') ? (
                <iframe
                  src={selectedVideo.video_url.replace('vimeo.com/', 'player.vimeo.com/video/')}
                  className="w-full h-full"
                  allowFullScreen
                  title={selectedVideo.title}
                />
              ) : (
                <video
                  src={selectedVideo.video_url}
                  controls
                  playsInline
                  disablePictureInPicture
                  // eslint-disable-next-line react/no-unknown-property
                  webkit-playsinline="true"
                  controlsList="nodownload"
                  className="w-full h-full"
                  poster={selectedVideo.thumbnail}
                />
              )}
            </div>
            
            <div className="p-6">
              <h1 className="text-2xl font-bold text-white mb-2">{selectedVideo.title}</h1>
              {selectedVideo.description && (
                <p className="text-gray-400">{selectedVideo.description}</p>
              )}
              {selectedVideo.duration && (
                <div className="flex items-center gap-2 mt-4 text-gray-500">
                  <FaClock />
                  <span>{selectedVideo.duration}</span>
                </div>
              )}
            </div>
          </motion.div>

          {/* Lista de otros videos */}
          <h3 className="text-lg font-semibold text-white mb-4">Más videos de esta carpeta</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {videos.filter(v => v.id !== selectedVideo.id).map((video) => (
              <div
                key={video.id}
                onClick={() => setSelectedVideo(video)}
                className="cursor-pointer rounded-xl overflow-hidden transition-all hover:scale-105"
                style={{ background: 'rgba(255, 255, 255, 0.05)' }}
              >
                <div className="relative aspect-video">
                  <img 
                    src={video.thumbnail || 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=200&h=150&fit=crop'} 
                    alt={video.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                    <FaPlay className="text-white text-2xl" />
                  </div>
                </div>
                <div className="p-2">
                  <p className="text-sm text-white truncate">{video.title}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4" style={{ backgroundColor: '#0a0a0a' }}>
      <div className="max-w-6xl mx-auto">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/recetarios')} 
          className="mb-8 text-gray-400 hover:text-white"
        >
          <FaArrowLeft className="mr-2" /> Volver a Recetarios
        </Button>

        {/* Header de la carpeta */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-start gap-6 flex-col md:flex-row">
            {/* Thumbnail */}
            <div className="w-full md:w-64 aspect-video rounded-xl overflow-hidden flex-shrink-0">
              <img 
                src={folder.thumbnail || 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400&h=300&fit=crop'} 
                alt={folder.name}
                className="w-full h-full object-cover"
              />
            </div>
            
            {/* Info */}
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl font-bold text-white">{folder.name}</h1>
                {(hasAccess || folder.is_free) ? (
                  <Badge className="bg-green-500 text-white">
                    <FaCheck className="mr-1" /> Acceso Completo
                  </Badge>
                ) : (
                  <Badge className="bg-[var(--brand-primary)] text-white">
                    <FaLock className="mr-1" /> Premium
                  </Badge>
                )}
              </div>
              
              {folder.description && (
                <p className="text-gray-400 mb-4">{folder.description}</p>
              )}
              
              <div className="flex items-center gap-4 text-gray-400">
                <span className="flex items-center gap-2">
                  <FaPlay className="text-[#00F0FF]" />
                  {videos.length} videos
                </span>
              </div>

              {/* Botón de compra si no tiene acceso */}
              {!hasAccess && !folder.is_free && folder.price > 0 && (
                <div className="mt-4 p-4 rounded-xl" style={{ background: 'rgba(var(--brand-primary-glow), 0.1)', border: '1px solid rgba(var(--brand-primary-glow), 0.3)' }}>
                  <p className="text-gray-300 mb-2">Obtén acceso a todos los videos de esta carpeta</p>
                  <div className="flex items-center gap-4">
                    <span className="text-2xl font-bold text-white">
                      ${folder.price?.toLocaleString('es-CO')} COP
                    </span>
                    <Button 
                      className="rounded-full text-white"
                      style={{ background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' }}
                      onClick={() => toast.info('Función de compra próximamente')}
                    >
                      Comprar Acceso
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Grid de videos */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {videos.map((video, index) => (
            <motion.div
              key={video.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              onClick={() => handleVideoClick(video)}
              className="group cursor-pointer rounded-xl overflow-hidden transition-all hover:scale-[1.02]"
              style={{ 
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}
            >
              {/* Thumbnail */}
              <div className="relative aspect-video overflow-hidden">
                <img 
                  src={video.thumbnail || 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400&h=300&fit=crop'} 
                  alt={video.title}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                />
                
                {/* Play overlay */}
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  {(hasAccess || folder.is_free) ? (
                    <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
                      <FaPlay className="text-white text-2xl ml-1" />
                    </div>
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-black/50 backdrop-blur flex items-center justify-center">
                      <FaLock className="text-white text-xl" />
                    </div>
                  )}
                </div>

                {/* Duration badge */}
                {video.duration && (
                  <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                    {video.duration}
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="p-4">
                <h3 className="text-lg font-semibold text-white group-hover:text-[var(--brand-primary)] transition-colors line-clamp-2">
                  {video.title}
                </h3>
                {video.description && (
                  <p className="text-gray-400 text-sm mt-1 line-clamp-2">{video.description}</p>
                )}
              </div>
            </motion.div>
          ))}
        </div>

        {videos.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No hay videos en esta carpeta aún.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RecetarioFolder;
